import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import * as actions from '../store/modules/auth/actions';
import { obterResumo } from './tributosApi';

import Painel from './pages/Painel';
import Recebimentos from './pages/Recebimentos';
import Conferencia from './pages/Conferencia';
import Produtos from './pages/Produtos';
import ProdutoForm from './pages/ProdutoForm';
import AlteracaoNcm from './pages/AlteracaoNcm';

import './tributos.css';

const INTERVALO_ATUALIZACAO_MS = 30000;

const MENU = [
  { to: '/tributos', label: 'Painel', icon: 'pi pi-th-large', end: true },
  { to: '/tributos/recebimentos', label: 'Recebimentos', icon: 'pi pi-inbox', badge: true },
  { to: '/tributos/produtos', label: 'Base de tributos', icon: 'pi pi-database' },
  { to: '/tributos/ncm', label: 'Alteração por NCM', icon: 'pi pi-sliders-h' },
];

/**
 * Sistema eloTributos — aberto quando o usuário logado é do tipo TRIBUTOS.
 * Possui layout próprio, independente do layout do eloAdmin.
 */
export default function TributosApp() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(state => state.auth.user) || {};

  const [resumo, setResumo] = useState(null);
  const [menuAberto, setMenuAberto] = useState(false);

  const atualizarResumo = useCallback(() => {
    obterResumo().then(setResumo).catch(() => {});
  }, []);

  useEffect(() => {
    atualizarResumo();
    const timer = setInterval(atualizarResumo, INTERVALO_ATUALIZACAO_MS);
    return () => clearInterval(timer);
  }, [atualizarResumo]);

  useEffect(() => {
    setMenuAberto(false);
  }, [location.pathname]);

  useEffect(() => {
    document.title = resumo && resumo.pendentes ? `(${resumo.pendentes}) eloTributos` : 'eloTributos';
  }, [resumo]);

  const sair = () => {
    document.title = 'eloAdmin';
    dispatch(actions.loginFailure());
    navigate('/login');
  };

  const pendentes = resumo ? resumo.pendentes : 0;

  return (
    <div className={`tb-app ${menuAberto ? 'tb-menu-open' : ''}`}>
      <aside className="tb-sidebar">
        <div className="tb-brand">
          <span className="tb-brand-mark">%</span>
          <div>
            <strong>eloTributos</strong>
            <small>Conferência fiscal</small>
          </div>
        </div>
        <nav className="tb-nav">
          {MENU.map(item => (
            <NavLink key={item.to} to={item.to} end={item.end} className={({ isActive }) => `tb-nav-item ${isActive ? 'active' : ''}`}>
              <i className={item.icon} />
              <span>{item.label}</span>
              {item.badge && pendentes > 0 ? <em className="tb-badge">{pendentes > 999 ? '999+' : pendentes}</em> : null}
            </NavLink>
          ))}
        </nav>
        <div className="tb-sidebar-footer">
          <div className="tb-user">
            <span className="tb-avatar">{(user.nome || user.email || '?').substring(0, 1).toUpperCase()}</span>
            <div>
              <strong>{user.nome || 'Operador'}</strong>
              <small>{user.email}</small>
            </div>
          </div>
          <button type="button" className="tb-btn tb-btn-ghost tb-btn-block" onClick={sair}>
            <i className="pi pi-sign-out" /> Sair
          </button>
        </div>
      </aside>

      <div className="tb-overlay" onClick={() => setMenuAberto(false)} aria-hidden="true" />

      <div className="tb-main">
        <header className="tb-topbar">
          <button type="button" className="tb-icon-btn tb-only-mobile" onClick={() => setMenuAberto(true)} aria-label="Abrir menu">
            <i className="pi pi-bars" />
          </button>
          <span className="tb-topbar-title">eloTributos</span>
          <div className="tb-topbar-right">
            {pendentes > 0 ? (
              <button type="button" className="tb-pill tb-pill-warn" onClick={() => navigate('/tributos/recebimentos')}>
                <i className="pi pi-bell" /> {pendentes} aguardando conferência
              </button>
            ) : (
              <span className="tb-pill tb-pill-ok"><i className="pi pi-check-circle" /> Fila em dia</span>
            )}
          </div>
        </header>

        <main className="tb-content">
          <Routes>
            <Route path="/tributos" element={<Painel resumo={resumo} onAtualizar={atualizarResumo} />} />
            <Route path="/tributos/recebimentos" element={<Recebimentos onAlteracao={atualizarResumo} />} />
            <Route path="/tributos/conferencia/:id" element={<Conferencia onAlteracao={atualizarResumo} />} />
            <Route path="/tributos/produtos" element={<Produtos />} />
            <Route path="/tributos/produtos/novo" element={<ProdutoForm onAlteracao={atualizarResumo} />} />
            <Route path="/tributos/produtos/:id" element={<ProdutoForm onAlteracao={atualizarResumo} />} />
            <Route path="/tributos/ncm" element={<AlteracaoNcm onAlteracao={atualizarResumo} />} />
            <Route path="*" element={<Navigate to="/tributos" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
